import React, { useState } from 'react';
import { formatInTimeZone } from '../utils/timezone';
import './GogglesConnectivityTimeline.css';

const CRITICAL_ERROR_NAMES = [
  'GogglesConfigFailedToSync',
  'GogglesForgetFailedToSync',
  'MemoryWarning',
  'GogglesOTAFailed',
  'BleWriteAckWedged'
];

const formatDuration = (ms) => {
  if (!ms || ms < 0) return '0s';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const clampPct = (v) => Math.min(100, Math.max(0, v));

const disconnectDisplay = (c) => {
  if (c.endTime == null) return { text: 'Still connected', tone: 'dim' };
  if (c.disconnectReason?.startsWith('Inferred:')) return { text: c.disconnectReason, tone: 'amber' };
  if (c.endedByPowerOff || c.disconnectIsTimeout) return { text: c.disconnectReason, tone: '' };
  if (c.disconnectReason) return { text: c.disconnectReason, tone: 'orange' };
  if (c.disconnectConfirmed) return { text: 'Clean disconnect', tone: 'dim' };
  return { text: 'Unknown — no GogglesDisconnected', tone: 'amber' };
};

/**
 * A connection usually has one battery reading, a few seconds after it starts.
 * With more than one, the first and last show the change across the connection.
 */
const batteryText = (readings, fmtTime) => {
  const describe = (r) => `${r.level}% at ${fmtTime(r.time)}`;
  const first = readings[0];
  const last = readings[readings.length - 1];
  return readings.length === 1 ? describe(first) : `${describe(first)} → ${describe(last)}`;
};

const OTA_OUTCOME_LEGEND = [
  { outcome: 'succeeded', label: 'OTA succeeded' },
  { outcome: 'failed', label: 'OTA failed' },
  { outcome: 'no_outcome', label: 'OTA no outcome' }
];

const otaOutcomeDisplay = (a) => {
  if (a.outcome === 'succeeded') return { text: 'Succeeded', tone: 'green' };
  if (a.outcome === 'failed') return { text: a.outcomeDetail ? `Failed: ${a.outcomeDetail}` : 'Failed', tone: 'orange' };
  return { text: a.outcomeDetail ?? 'No outcome logged', tone: 'amber' };
};

const otaVersionLabel = (a) => {
  const from = a.fromVersion ? `v${a.fromVersion}` : null;
  const to = a.toVersion ? `v${a.toVersion}` : null;
  return from && to ? `${from} → ${to}` : to ? `→ ${to}` : from;
};

/**
 * Goggles Connectivity Timeline — a synthesized panel derived from a
 * session's events (see src/utils/gogglesConnectivityTimeline.js). Renders
 * whenever the session has goggles activity or a resort ping; the caller is
 * responsible for not rendering this component when derive() returns null.
 *
 * See plans/goggles-connectivity-timeline/ for the full spec.
 */
const GogglesConnectivityTimeline = ({ data, sessionStartTime, sessionEndTime, timeZone }) => {
  const [criticalInfoOpen, setCriticalInfoOpen] = useState(false);

  if (!data) return null;

  const sessionStartMs = new Date(sessionStartTime).getTime();
  const sessionEndMs = new Date(sessionEndTime).getTime();
  const sessionSpanMs = Math.max(1, sessionEndMs - sessionStartMs);
  const pctOf = (ms) => clampPct(((ms - sessionStartMs) / sessionSpanMs) * 100);

  const fmtTime = (ms) =>
    ms == null ? 'Unknown' : formatInTimeZone(ms, timeZone, { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });

  const {
    resortId,
    gogglesPairedBeforeSkiing,
    longConnections,
    shortConnections,
    gogglesConnectFailureEpisodes,
    gogglesOtaAttempts,
    connectedMs
  } = data;

  const connectedPct = Math.round((connectedMs / sessionSpanMs) * 100);
  const otaSucceededCount = gogglesOtaAttempts.filter((a) => a.outcome === 'succeeded').length;

  const fmtOtaTimes = (a) => {
    if (!a.startConfirmed) return `Start not in this session – ${fmtTime(a.endTime)}`;
    if (a.endTime == null) return `${fmtTime(a.startTime)} – no end logged`;
    return `${fmtTime(a.startTime)} – ${fmtTime(a.endTime)} · ${formatDuration(a.durationMs)}`;
  };

  // Critical point events: one-off errors (excluding GogglesFailedToConnect,
  // which is represented as its own hatched span, not a diamond) plus GogglesForgotten.
  const criticalEvents = (data.events || []).filter(
    (e) => CRITICAL_ERROR_NAMES.includes(e.eventName) || e.eventName === 'GogglesForgotten'
  );

  return (
    <div className="gct-panel">
      <div className="gct-header">
        <span className="gct-icon">🥽</span>
        <h3 className="gct-title">Goggles Connectivity Timeline</h3>
        <div className="gct-badges">
          {resortId && <span className="gct-resort-chip">{resortId}</span>}
          {resortId && (
            <span className={`gct-goggles-badge ${gogglesPairedBeforeSkiing ? 'paired' : 'not-paired'}`}>
              {gogglesPairedBeforeSkiing ? '✓ Goggles' : '— Goggles'}
            </span>
          )}
        </div>
      </div>

      <div className="gct-stats">
        <div className="gct-stat">
          <label>Goggles Connected</label>
          <span className="gct-val blue">
            {formatDuration(connectedMs)} <span className="gct-pct">({connectedPct}%)</span>
          </span>
        </div>
        {gogglesConnectFailureEpisodes.length > 0 && (
          <div className="gct-stat">
            <label>Connect Failures</label>
            <span className="gct-val red">
              {gogglesConnectFailureEpisodes.length} episode{gogglesConnectFailureEpisodes.length !== 1 ? 's' : ''}{' '}
              <span className="gct-pct">
                ({gogglesConnectFailureEpisodes.reduce((n, ep) => n + ep.failureCount, 0)} attempts)
              </span>
            </span>
          </div>
        )}
        {gogglesOtaAttempts.length > 0 && (
          <div className="gct-stat">
            <label>OTA Updates</label>
            <span className={`gct-val ${otaSucceededCount > 0 ? 'green' : 'red'}`}>
              {otaSucceededCount} succeeded{' '}
              <span className="gct-pct">
                ({gogglesOtaAttempts.length} attempt{gogglesOtaAttempts.length !== 1 ? 's' : ''})
              </span>
            </span>
          </div>
        )}
      </div>

      <div className="gct-timeline">
        <div className="gct-track">
          <div className="gct-session-span" style={{ left: '0%', width: '100%' }} />
          {longConnections.map((c, i) => {
            const unknownEnd = c.endTime != null && !c.disconnectConfirmed;
            return (
              <div
                key={`conn-${i}`}
                className={`gct-segment${unknownEnd ? ' unknown-end' : ''}`}
                style={{ left: `${pctOf(c.startTime)}%`, width: `${pctOf(c.endTime ?? sessionEndMs) - pctOf(c.startTime)}%` }}
                title={
                  unknownEnd
                    ? `Connected ${fmtTime(c.startTime)} – ${fmtTime(c.endTime)} · end reason unknown, no GogglesDisconnected`
                    : `Connected ${fmtTime(c.startTime)} – ${fmtTime(c.endTime)}`
                }
              />
            );
          })}
          {gogglesConnectFailureEpisodes.map((ep, i) => (
            <div
              key={`fail-${i}`}
              className="gct-segment failing"
              style={{ left: `${pctOf(ep.startTime)}%`, width: `${Math.max(0.5, pctOf(ep.endTime) - pctOf(ep.startTime))}%` }}
              title={`Failing to connect ${fmtTime(ep.startTime)} – ${fmtTime(ep.endTime)} · ${ep.failureCount} attempts`}
            />
          ))}
          {data.resortDetectedAt != null && (
            <div className="gct-ping" style={{ left: `${pctOf(data.resortDetectedAt)}%` }}>
              <span className="gct-ping-dot" title={`User at Resort · ${fmtTime(data.resortDetectedAt)}`} />
            </div>
          )}
        </div>
        {gogglesOtaAttempts.length > 0 && (
          <div className="gct-ota-lane">
            {gogglesOtaAttempts.map((a, i) => (
              <div
                key={`ota-${i}`}
                className={`gct-ota-span ${a.outcome}`}
                style={{ left: `${pctOf(a.startTime)}%`, width: `${pctOf(a.endTime ?? sessionEndMs) - pctOf(a.startTime)}%` }}
                title={[otaVersionLabel(a) && `OTA ${otaVersionLabel(a)}`, fmtOtaTimes(a), otaOutcomeDisplay(a).text]
                  .filter(Boolean)
                  .join(' · ')}
              />
            ))}
          </div>
        )}
        <div className="gct-ticks">
          {criticalEvents.map((e, i) => (
            <div key={`crit-${i}`} className="gct-tick" style={{ left: `${pctOf(new Date(e.timestamp).getTime())}%` }}>
              <span className="gct-diamond" />
              <span className="gct-tick-tip">
                {e.eventName} · {fmtTime(new Date(e.timestamp).getTime())}
              </span>
            </div>
          ))}
        </div>
        <div className="gct-legend">
          <div className="gct-legend-item">
            <span className="gct-leg-swatch dashed" />
            Session span
          </div>
          <div className="gct-legend-item">
            <span className="gct-leg-swatch connected" />
            Goggles connected
          </div>
          <div className="gct-legend-item">
            <span className="gct-leg-swatch failing" />
            Failing to connect
          </div>
          {longConnections.some((c) => c.endTime != null && !c.disconnectConfirmed) && (
            <div className="gct-legend-item">
              <span className="gct-leg-swatch unknown-end" />
              End reason unknown
            </div>
          )}
          {data.resortDetectedAt != null && (
            <div className="gct-legend-item">
              <span className="gct-leg-swatch ping" />
              User at Resort
            </div>
          )}
          {OTA_OUTCOME_LEGEND.filter(({ outcome }) => gogglesOtaAttempts.some((a) => a.outcome === outcome)).map(
            ({ outcome, label }) => (
              <div className="gct-legend-item" key={outcome}>
                <span className={`gct-leg-swatch ota ${outcome}`} />
                {label}
              </div>
            )
          )}
          <div className="gct-legend-item gct-critical-legend">
            <span className="gct-leg-swatch critical" />
            Critical Events
            <button
              type="button"
              className="gct-info-btn"
              aria-label="What counts as a critical event"
              onClick={() => setCriticalInfoOpen((v) => !v)}
            >
              ⓘ
            </button>
            {criticalInfoOpen && (
              <div className="gct-info-popup">
                <p className="gct-info-title">What counts as a critical event</p>
                <ul>
                  <li>
                    <b>BleWriteAckWedged</b> — goggles stayed connected but stopped acknowledging BLE writes
                  </li>
                  <li>
                    <b>GogglesForgotten</b> — pairing was explicitly removed mid-session
                  </li>
                  <li>
                    <b>GogglesFailedToConnect</b> episodes — shown as their own hatched span, not a diamond
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {gogglesOtaAttempts.length > 0 && (
        <details className="gct-connections-details" open>
          <summary className="gct-connections-label">OTA Updates ({gogglesOtaAttempts.length})</summary>
          <div className="gct-connections">
            {gogglesOtaAttempts.map((a, i) => (
              <div className="gct-conn-card" key={i}>
                <div className="gct-conn-head">
                  <span className="gct-conn-num">Attempt {i + 1}</span>
                  {otaVersionLabel(a) && <span className="gct-conn-badge">{otaVersionLabel(a)}</span>}
                </div>
                <div className="gct-conn-time">{fmtOtaTimes(a)}</div>
                <div className="gct-meta-row">
                  <span className="gct-k">outcome</span>
                  <span className={`gct-v ${otaOutcomeDisplay(a).tone}`}>{otaOutcomeDisplay(a).text}</span>
                </div>
                {a.firmwareChannel && (
                  <div className="gct-meta-row">
                    <span className="gct-k">firmwareChannel</span>
                    <span className="gct-v">{a.firmwareChannel}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {longConnections.length > 0 && (
        <details className="gct-connections-details" open>
          <summary className="gct-connections-label">
            Goggles Connections · 5+ min ({longConnections.length})
          </summary>
          <div className="gct-connections">
            {longConnections.map((c, i) => (
              <div className="gct-conn-card" key={i}>
                <div className="gct-conn-head">
                  <span className="gct-conn-num">Connection {i + 1}</span>
                  {c.gogglesHwModel && <span className="gct-conn-badge">HW V{c.gogglesHwModel}</span>}
                  {c.gogglesSwVersion && <span className="gct-conn-badge">v{c.gogglesSwVersion}</span>}
                </div>
                <div className="gct-conn-time">
                  {fmtTime(c.startTime)} – {c.endTime ? fmtTime(c.endTime) : 'still connected'} · {formatDuration(c.durationMs)}
                </div>
                {c.pairingPin && (
                  <div className="gct-meta-row">
                    <span className="gct-k">goggles_pin</span>
                    <span className="gct-v">{c.pairingPin}</span>
                  </div>
                )}
                <div className="gct-meta-row">
                  <span className="gct-k">disconnectReason</span>
                  <span className={`gct-v ${disconnectDisplay(c).tone}`}>{disconnectDisplay(c).text}</span>
                </div>
                {c.rssiAtDisconnect != null && (
                  <div className="gct-meta-row">
                    <span className="gct-k">RSSI at Disconnect</span>
                    <span className="gct-v">
                      {c.rssiAtDisconnect} dBm
                      {c.rssiAgeMs != null && <span className="gct-pct"> · read {formatDuration(c.rssiAgeMs)} before</span>}
                    </span>
                  </div>
                )}
                {c.batteryReadings.length > 0 && (
                  <div className="gct-meta-row">
                    <span className="gct-k">Battery</span>
                    <span className="gct-v">{batteryText(c.batteryReadings, fmtTime)}</span>
                  </div>
                )}
                {c.lastResetReason && (
                  <div className="gct-meta-row">
                    <span className="gct-k">lastResetReason</span>
                    <span className={`gct-v ${c.lastResetWasCrash ? 'orange' : 'dim'}`}>{c.lastResetReason}</span>
                  </div>
                )}
                <div className="gct-meta-row">
                  <span className="gct-k">gogglesCrashReported</span>
                  <span className={`gct-v ${c.gogglesCrashReported ? 'orange' : 'dim'}`}>
                    {c.gogglesCrashReported ? '⚠ Yes' : 'No'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {shortConnections.length > 0 && (
        <details className="gct-short-conns">
          <summary>
            <span className="gct-short-count">{shortConnections.length} short connection{shortConnections.length !== 1 ? 's' : ''}</span>
            <span className="gct-short-hint">&lt; 5 min</span>
          </summary>
          <div className="gct-short-list">
            {shortConnections.map((c, i) => (
              <div className="gct-short-row" key={i}>
                <span>
                  {fmtTime(c.startTime)} – {c.endTime ? fmtTime(c.endTime) : 'still connected'}
                </span>
                <span className="gct-dur">{formatDuration(c.durationMs)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default GogglesConnectivityTimeline;
